"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { useFormik } from "formik";
import {
  Button,
  Input,
  FormError,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/";
import { fieldTouchedError } from "@/lib/form-helpers";
import { Images } from "@/assests";
import { ThemedLogo } from "@/components/ui/themed-logo";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { footerColumns, socialLinks, legalLinks, aiSummaryLinks, newsletterSchema } from "./data";
import { STATS, STATUS_URL, TRUST_CENTER_URL } from "@/lib/constants";
import CookieSettingsButton from "@/components/site/cookie-consent/cookie-settings-button";

const complianceBadges = [
  {
    id: "iso27001",
    src: Images.iso27001,
    alt: "ISO 27001 Certified",
    name: "ISO 27001",
    status: "Certified",
    width: 1042,
    height: 1250,
  },
  {
    id: "soc2",
    src: Images.soc2,
    alt: "SOC 2 Certified",
    name: "SOC 2",
    status: "Certified",
    width: 359,
    height: 357,
  },
];

export default function Footer() {
  const [submitted, setSubmitted] = useState(false);
  const prefersLight = usePrefersLight();

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: newsletterSchema,
    onSubmit: async (values, { resetForm }) => {
      const body = new FormData();
      body.append("email", values.email);
      const res = await fetch("/api/subscribe", { method: "POST", body });
      if (!res.ok) return;
      setSubmitted(true);
      resetForm();
    },
  });

  return (
    <footer className="border-border bg-background border-t" aria-label="Site footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-6 lg:gap-12">
          <div className="contents md:col-span-2 md:block">
            <div className="order-1 col-span-2 md:order-none">
              <Link href="/" aria-label="Proxidize — Home">
                <ThemedLogo width={187} height={42} className="mb-4" />
              </Link>
              <p className="text-muted-foreground max-w-xs text-sm leading-relaxed">
                Managed cloud proxies built for developers, enterprises, and AI teams. {STATS.ips}{" "}
                premium mobile and residential IPs across {STATS.countries} countries. No hardware
                or DIY setup required.
              </p>
            </div>

            <div className="order-3 col-span-2 md:order-none md:mt-6">
              <div className="flex justify-center gap-3 md:justify-start">
                {socialLinks.map(({ label, href, icon }) => (
                  <Link
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="text-muted-foreground hover:text-foreground flex h-14 w-14 items-center justify-center rounded-md transition-colors"
                  >
                    {icon}
                  </Link>
                ))}
              </div>

              <div className="mt-8">
                <p className="text-foreground mb-2 text-sm font-semibold">Stay in the loop</p>
                <p className="text-muted-foreground mb-3 text-xs">
                  Get product updates and news delivered to your inbox.
                </p>
                {submitted ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="h-4 w-4 shrink-0 text-green-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      You&apos;re subscribed — thank you!
                    </span>
                  </div>
                ) : (
                  <form onSubmit={formik.handleSubmit} className="flex gap-2" noValidate>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <Input
                        type="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="you@example.com"
                        className="bg-muted/50 h-9 w-full"
                        aria-invalid={!!(formik.touched.email && formik.errors.email)}
                      />
                      {fieldTouchedError(formik, "email") && (
                        <FormError>{fieldTouchedError(formik, "email")}</FormError>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={formik.isSubmitting}
                      size="sm"
                      variant="brand"
                      className="h-9 shrink-0 self-start"
                    >
                      {formik.isSubmitting ? "…" : "Subscribe"}
                    </Button>
                  </form>
                )}

                <div className="mt-6">
                  <p className="text-foreground mb-3 text-sm font-semibold">
                    Security &amp; compliance
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {complianceBadges.map((badge) => (
                      <a
                        key={badge.id}
                        href={TRUST_CENTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-2.5"
                      >
                        <Image
                          src={badge.src}
                          alt=""
                          width={badge.width}
                          height={badge.height}
                          className="h-12.5 w-auto shrink-0 object-contain"
                        />
                        <span className="text-xs leading-tight">
                          <span className="text-foreground block font-medium">{badge.name}</span>
                          <span className="text-muted-foreground block">{badge.status}</span>
                        </span>
                        <span className="sr-only"> — view our Trust Center (opens in new tab)</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-2 col-span-2 columns-2 gap-x-8 md:contents">
            {footerColumns.map((col) => (
              <div
                key={col.sections[0]?.heading ?? col.sections.map((s) => s.heading).join("-")}
                className="mb-8 flex break-inside-avoid flex-col gap-6 last:mb-0 md:mb-0 md:gap-8"
              >
                {col.sections.map(({ heading, links }) => (
                  <div key={heading}>
                    <p className="text-foreground mb-4 text-sm font-semibold">{heading}</p>
                    <ul className="space-y-3">
                      {links.map(({ label, href }) => {
                        const isExternal = href.startsWith("http");
                        return (
                          <li key={label}>
                            <Link
                              href={href}
                              {...(isExternal
                                ? { target: "_blank" as const, rel: "nofollow noopener noreferrer" }
                                : {})}
                              className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                            >
                              {label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="border-border -mx-4 mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 border-t px-4 pt-5 sm:-mx-6 sm:justify-between sm:px-6 lg:-mx-8 lg:px-8">
          <p className="text-muted-foreground hidden text-center text-sm sm:block sm:text-left">
            © 2026 Proxidize | All Rights Reserved
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
            <span className="text-muted-foreground text-xs font-medium">AI Summary</span>
            <TooltipProvider delay={0} closeDelay={0}>
              <div className="flex items-center gap-1.5">
                {aiSummaryLinks.map(({ name, label, buildUrl, icon }) => (
                  <Tooltip key={label}>
                    <TooltipTrigger
                      onClick={() => {
                        const pageUrl = typeof window !== "undefined" ? window.location.href : "";
                        window.open(buildUrl(pageUrl), "_blank", "noopener,noreferrer");
                      }}
                      aria-label={label}
                      className="text-muted-foreground hover:text-foreground hover:bg-muted flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                    >
                      {icon}
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs font-semibold">
                      {name}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>
        </div>

        <div className="border-border/60 -mx-4 mt-4 flex flex-col gap-4 border-t px-4 pt-4 sm:-mx-6 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="grid grid-cols-2 gap-x-5 gap-y-2 sm:flex sm:flex-wrap">
            {legalLinks.map(({ label, href, dialog }) => {
              if (dialog === "cookie") {
                // Always rendered, in every region. Opens CookieYes's
                // preference center where the SDK provides one, and degrades
                // to a privacy-policy link where it doesn't.
                return <CookieSettingsButton key={label} label={label} fallbackHref={href} />;
              }
              return (
                <Link
                  key={label}
                  href={href}
                  className="text-muted-foreground hover:text-foreground text-xs whitespace-nowrap transition-colors"
                >
                  {label}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() =>
                (window as { UserWay?: { widgetOpen: () => void } }).UserWay?.widgetOpen()
              }
              className="text-muted-foreground hover:text-foreground cursor-pointer text-left text-xs whitespace-nowrap transition-colors"
            >
              Accessibility Menu
            </button>
          </div>
          <div className="flex shrink-0 justify-center sm:ml-auto sm:justify-end">
            <iframe
              src={`${STATUS_URL}/badge?theme=${prefersLight ? "light" : "dark"}`}
              width="190"
              height="30"
              loading="lazy"
              title="Proxidize Status Page - Opens in a new tab"
              aria-label="Proxidize Status Page - Opens in a new tab"
              className="block border-0"
            />
          </div>
        </div>

        <p className="border-border/60 text-muted-foreground -mx-4 mt-6 border-t px-4 pt-4 text-center text-sm sm:-mx-6 sm:hidden sm:px-6">
          © 2026 Proxidize | All Rights Reserved
        </p>
      </div>

      {/* Giant brand wordmark, clipped by the bottom page edge */}
      <div
        aria-hidden="true"
        className="pointer-events-none -mt-8 overflow-hidden select-none sm:-mt-12"
      >
        <p className="from-foreground/12 to-foreground/2 -mb-[0.16em] bg-gradient-to-b bg-clip-text text-center text-[clamp(4.5rem,19vw,19rem)] leading-none font-bold tracking-tighter whitespace-nowrap text-transparent">
          Proxidize
        </p>
      </div>
    </footer>
  );
}
